"use client";

import {
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import IImportationDetails, { ImportationDetailsStatus } from "@/interfaces/importation-details";
import { ImportationDetailsFiltersMessages } from "@/messages/importationDetails/importation-details-filter.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import { getInitialFilterValues } from "../../constants";
import { ImportationDetailsFilterKey } from "../../types";

const GRID_SIZES = {
  LARGE: { lg: 3, md: 4, sm: 6, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const STATUS_VALUES = {
  CREATED: ImportationDetailsStatus.CREATED,
  UPDATED: ImportationDetailsStatus.UPDATED,
  WARNING: ImportationDetailsStatus.WARNING,
  ERROR: ImportationDetailsStatus.ERROR,
  EMPTY: "",
} as const;

type Props = {
  messages: ImportationDetailsFiltersMessages;
  importProcessId: string;
};

function FiltersForm({ messages, importProcessId }: Props) {
  const { values, setValues, submitForm, errors } =
    useFormikContext<IFilter<IImportationDetails>[]>();

  const getValueFromProperty = (
    property: string,
    filterValues: IFilter<IImportationDetails>[]
  ): IFilter<IImportationDetails> | undefined => {
    const filter = filterValues.find((f) => f.property === property);
    return filter;
  };

  const handleFilterChange = (
    property: ImportationDetailsFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const currentFilters = [...prevFilters];

      const existingFilterIndex = currentFilters.findIndex((f) => f.property === property);
      if (existingFilterIndex !== -1) {
        currentFilters.splice(existingFilterIndex, 1);
      }

      const isEmptyValue =
        value === null || value === "" || (typeof value === "number" && isNaN(value));
      if (isEmptyValue) {
        return currentFilters;
      }

      const newFilter: IFilter<IImportationDetails> = {
        property: property as keyof IImportationDetails,
        filterType: filterType,
        value: value,
      };

      return [...currentFilters, newFilter];
    });
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const statusValue = selectedValue === STATUS_VALUES.EMPTY ? null : selectedValue;
    handleFilterChange("status", statusValue, FilterType.Equals);
  };

  const handleClearFilters = () => {
    setValues(getInitialFilterValues(importProcessId));
  };

  const handleSubmitFilters = () => {
    const userFilters = values.filter((filter) => filter.property !== "importProcessId");
    const finalFilters = [...getInitialFilterValues(importProcessId), ...userFilters];
    setValues(finalFilters);
    submitForm();
  };

  const renderTextField = (
    name: string,
    label: string,
    property: ImportationDetailsFilterKey,
    filterType: FilterType = FilterType.Contains
  ) => (
    <Grid size={GRID_SIZES.LARGE}>
      <TextField
        name={name}
        label={label}
        onChange={(e) => handleFilterChange(property, e.target.value, filterType)}
        value={getValueFromProperty(property, values)?.value ?? ""}
        error={!!errors[0] && !!errors[0].value}
        helperText={!!errors[0] && errors[0].value}
        fullWidth
        slotProps={{
          inputLabel: { shrink: true },
        }}
      />
    </Grid>
  );

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          {renderTextField("code", messages.codeOption, "code")}
          {renderTextField("name", messages.nameOption, "name")}
          {renderTextField("message", messages.messageOption, "message")}

          <Grid size={GRID_SIZES.LARGE}>
            <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
              <InputLabel shrink>{messages.statusOption}</InputLabel>
              <Select
                value={String(getValueFromProperty("status", values)?.value ?? STATUS_VALUES.EMPTY)}
                onChange={handleStatusChange}
                label={messages.statusOption}
                displayEmpty
                inputProps={{ "aria-label": messages.statusOption }}
              >
                <MenuItem value={STATUS_VALUES.EMPTY}>
                  {messages.selectStatusPlaceholder || "Selecionar status"}
                </MenuItem>
                <MenuItem value={STATUS_VALUES.CREATED}>{messages.createdOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.UPDATED}>{messages.updatedOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.WARNING}>{messages.warningOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.ERROR}>{messages.errorOption}</MenuItem>
              </Select>
              {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Grid container spacing={2} mt={2}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  {messages.clearButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={handleSubmitFilters} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
